import { accountGeneralInfoSchema } from '@/lib/accountFormValidators';
import { extractErrorMessage } from '@/lib/errorUtils';
import { accountFormService } from '@/services/accountFormService';
import { AccountApiFormData, AccountFormData } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

/**
 * Hook for token validation
 */
export const useTokenValidation = (
  token: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['validateToken', token],
    queryFn: () => accountFormService.validateToken(token),
    retry: false,
    staleTime: 0, // Always check token freshness
    gcTime: 0, // Don't cache token validation results
    enabled: options?.enabled !== false, // Default to enabled unless explicitly disabled
  });
};

/**
 * Hook for getting form field options
 */
export const useFormOptions = () => {
  return useQuery({
    queryKey: ['formOptions'],
    queryFn: () => accountFormService.getFormOptions(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * Hook for getting postal codes based on country
 */
export const usePostalCodes = (country: string, enabled = true) => {
  return useQuery({
    queryKey: ['postalCodes', country],
    queryFn: () => accountFormService.getPostalCodes(country),
    enabled: enabled && !!country,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * Hook for getting cities based on country and postal code
 */
export const useCities = (
  country: string,
  postalCode: string,
  enabled = true
) => {
  return useQuery({
    queryKey: ['cities', country, postalCode],
    queryFn: () => accountFormService.getCities(country, postalCode),
    enabled: enabled && !!country && !!postalCode,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * Hook for getting existing account data
 */
export const useAccountData = (token: string) => {
  return useQuery({
    queryKey: ['accountData', token],
    queryFn: () => accountFormService.getAccountData(token),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for account form submission
 */
export const useAccountFormSubmission = (token: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AccountFormData) =>
      accountFormService.submitAccountForm(token, data),
    onSuccess: response => {
      toast.success('Success', {
        description:
          response.message || 'Account information updated successfully',
      });

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['accountData', token] });
    },
    onError: (error: Error) => {
      const errorMessage = extractErrorMessage(
        error,
        'Failed to update account information'
      );
      toast.error('Error', {
        description: errorMessage,
      });
    },
  });
};

/**
 * Main hook for account form state management
 */
export const useAccountForm = (
  token: string,
  initialData?: AccountFormData
) => {
  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountGeneralInfoSchema),
    defaultValues: {
      companyType: '',
      language: '',
      name: '',
      nameAddition: '',
      country: '',
      street: '',
      streetNo: '',
      poBox: '',
      postalCode: '',
      city: '',
      canton: '',
      website: '',
      industry: '',
      commercialRegisterNumber: '',
      ...initialData,
    },
  });

  const submitMutation = useAccountFormSubmission(token);

  // Watch country and postal code for dependent field updates
  const watchedCountry = form.watch('country');
  const watchedPostalCode = form.watch('postalCode');

  // Get form options
  const { data: formOptions, isLoading: isLoadingOptions } = useFormOptions();

  // Get postal codes based on selected country
  const { data: postalCodes, isLoading: isLoadingPostalCodes } = usePostalCodes(
    watchedCountry,
    !!watchedCountry
  );

  // Get cities based on selected country and postal code
  const { data: cities, isLoading: isLoadingCities } = useCities(
    watchedCountry,
    watchedPostalCode,
    !!watchedCountry && !!watchedPostalCode
  );

  // Reset dependent fields when country changes
  const handleCountryChange = (country: string) => {
    form.setValue('country', country);
    form.setValue('postalCode', '');
    form.setValue('city', '');
    form.setValue('canton', '');
  };

  // Reset city when postal code changes
  const handlePostalCodeChange = (postalCode: string) => {
    form.setValue('postalCode', postalCode);
    form.setValue('city', '');
  };

  const onSubmit = async (data: AccountFormData) => {
    await submitMutation.mutateAsync(data);
  };

  return {
    form,
    onSubmit,
    isSubmitting: submitMutation.isPending,
    submitError: submitMutation.error,
    formOptions: {
      companyTypes: formOptions?.companyTypes || [],
      languages: formOptions?.languages || [],
      countries: formOptions?.countries || [],
      industries: formOptions?.industries || [],
      cantons: formOptions?.cantons || [],
      postalCodes: postalCodes || [],
      cities: cities || [],
    },
    isLoading: {
      options: isLoadingOptions,
      postalCodes: isLoadingPostalCodes,
      cities: isLoadingCities,
    },
    handlers: {
      handleCountryChange,
      handlePostalCodeChange,
    },
  };
};

/**
 * Hook for Account form submission using API format
 * This version accepts AccountApiFormData directly for use with the form adapter
 */
export const useAccountFormSubmissionApi = () => {
  return useMutation({
    mutationFn: (apiFormData: AccountApiFormData) =>
      accountFormService.submitAccountFormApi(apiFormData),
    onError: (error: Error) => {
      // Let the calling component handle the error display
      console.error('Account form submission error:', error);
    },
  });
};
